import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { PMDialog } from "../../../utils/PMDialog";
import { ResourceLoading } from "../../../utils/ResourceLoading";
import { getChecklistType } from "../../../../api/quality/checklist";
import { registerChecklistTypeHse } from "../../../../api/quality/checklistType";
import { DisciplineCategoryChapterSelector } from "../../documents/DisciplineCategoryChapterSelector";

export default function ChecklistTypeHseModal({ open, onClose, itemId, onRegistered }) {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [checklistType, setChecklistType] = useState(null);
  const [isRegisteringHse, setIsRegisteringHse] = useState(false);
  const [hseDisciplineId, setHseDisciplineId] = useState("");
  const [hseCategoryId, setHseCategoryId] = useState("");
  const [hseChapterNumber, setHseChapterNumber] = useState("");

  useEffect(() => {
    if (!open || !itemId) {
      setChecklistType(null);
      setHseDisciplineId("");
      setHseCategoryId("");
      setHseChapterNumber("");
      return;
    }

    const fetchType = async () => {
      setIsLoading(true);
      const res = await getChecklistType(itemId);
      const type = res?.status === 200 ? res?.data?.data : null;
      setChecklistType(type);
      const hse = type?.hse_document;
      setHseDisciplineId(hse?.discipline?.id ? String(hse.discipline.id) : "");
      setHseCategoryId(hse?.category?.id ? String(hse.category.id) : "");
      setHseChapterNumber(hse?.chapter?.number ? String(hse.chapter.number) : "");
      setIsLoading(false);
    };

    fetchType();
  }, [open, itemId]);

  const registerInHms = async () => {
    if (!checklistType?.id) {
      toast.error(t("quality.checklist.type.hse.save_type_first"));
      return;
    }
    if (!hseCategoryId) {
      toast.error(t("quality.checklist.type.hse.category_required"));
      return;
    }
    setIsRegisteringHse(true);
    const res = await registerChecklistTypeHse(checklistType.id, {
      discipline_id: hseDisciplineId || undefined,
      category_id: hseCategoryId,
      chapter_number: hseChapterNumber || undefined,
    });
    setIsRegisteringHse(false);
    if (res?.errors) {
      toast.error(
        Array.isArray(res.errors)
          ? res.errors.join(", ")
          : t("quality.checklist.type.hse.register_failed")
      );
      return;
    }
    toast.success(res?.message || t("quality.checklist.type.hse.registered"));
    const updated = res?.type || checklistType;
    if (res?.type) {
      setChecklistType(res.type);
    }
    onRegistered?.(updated);
  };

  const title =
    checklistType?.displayTitle || checklistType?.title || t("quality.checklist.type.hse.title");

  return (
    <PMDialog open={open} onClose={onClose} title={title} size="2xl" dense>
      {isLoading ? (
        <ResourceLoading size="base" />
      ) : (
        <div>
          <div className="text-sm font-semibold text-gray-900">
            {t("quality.checklist.type.hse.title")}
          </div>
          <p className="mt-1 text-sm text-gray-500">
            {t("quality.checklist.type.hse.description")}
          </p>

          <div className="mt-4 space-y-4">
            <DisciplineCategoryChapterSelector
              disciplineId={hseDisciplineId}
              categoryId={hseCategoryId}
              chapterNumber={hseChapterNumber}
              onDisciplineChange={setHseDisciplineId}
              onCategoryChange={(value) => {
                setHseCategoryId(value);
                setHseChapterNumber("");
              }}
              onChapterChange={setHseChapterNumber}
              required
            />
          </div>

          <div className="mt-6 flex flex-wrap items-center justify-end gap-3">
            {checklistType?.hse_document?.id ? (
              <Link
                to={`/dashboard/hse-ks/documents/${checklistType.hse_document.id}`}
                className="inline-flex text-sm font-semibold text-blue-700 hover:text-blue-900"
                onClick={onClose}
              >
                {t("quality.checklist.type.hse.open_document")} →
              </Link>
            ) : null}
            <button
              type="button"
              onClick={onClose}
              className="rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700"
            >
              {t("Cancel")}
            </button>
            <button
              type="button"
              disabled={isRegisteringHse || !hseCategoryId}
              onClick={registerInHms}
              className="rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
            >
              {isRegisteringHse
                ? t("Loading")
                : checklistType?.hse_document?.id
                  ? t("quality.checklist.type.hse.update_placement")
                  : t("quality.checklist.type.hse.register")}
            </button>
          </div>
        </div>
      )}
    </PMDialog>
  );
}
